import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ShieldAlert, LogIn, LogOut, XCircle, Clock,
    Filter, Search, Download, ChevronLeft, ChevronRight,
    Monitor, Smartphone, Tablet, Globe, RefreshCw, CheckCircle, AlertTriangle, ArrowLeft
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';

// ─── helpers ──────────────────────────────────────────────────────────────────

const ACTION_LABELS = {
    Login: { label: 'Login', color: 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400', Icon: LogIn },
    LoginFailed: { label: 'Failed', color: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400', Icon: XCircle },
    LoginBlocked: { label: 'Blocked', color: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400', Icon: ShieldAlert },
    OtpFailed: { label: 'OTP Failed', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-400', Icon: AlertTriangle },
    Logout: { label: 'Logout', color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400', Icon: LogOut },
};

const ROLE_COLORS = {
    admin: 'bg-blue-100/60 text-[#1a3884] dark:bg-[#1a3884]/30 dark:text-[#4f7dff]',
    college_admin: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400',
    student: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const fmt = (dt) => {
    if (!dt) return '—';
    const d = new Date(dt);
    return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
    });
};

const formatIP = (ip) => {
    if (!ip) return '—';
    if (ip === '::1' || ip === '::ffff:127.0.0.1') return '127.0.0.1';
    return ip;
};

const exportToCSV = (rows) => {
    if (!rows.length) return toast.error('No data to export');
    const headers = ['Timestamp', 'User', 'Email', 'Role', 'Action', 'Status', 'IP Address', 'Device', 'Details'];
    const escapeCsv = (val) => `"${String(val || '').replace(/"/g, '""')}"`;
    const lines = rows.map(r => [
        escapeCsv(fmt(r.createdAt)),
        escapeCsv(r.user?.fullName || '—'),
        escapeCsv(r.user?.email || r.targetEmail || '—'),
        escapeCsv(r.user?.role || '—'),
        escapeCsv(r.action),
        escapeCsv(r.success ? 'Success' : 'Failed'),
        escapeCsv(formatIP(r.ipAddress)),
        escapeCsv(r.deviceInfo || '—'),
        escapeCsv(r.details || '—'),
    ]);
    const csv = [headers.join(','), ...lines.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `access-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
};

// ─── stat card ────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, Icon, subtext }) => {
    return (
        <div className="bg-white dark:bg-[#002147] rounded-xl p-4 border border-gray-100 dark:border-[#1a3884]/35 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group">
            <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate mb-1">{label}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value ?? '—'}</p>
                    {subtext && <p className="text-[9px] text-slate-400 dark:text-slate-300 mt-0.5">{subtext}</p>}
                </div>
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] dark:from-[#002A5C] dark:to-[#1a3884] flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-5 h-5 text-white" />
                </div>
            </div>
        </div>
    );
};

// ─── main component ───────────────────────────────────────────────────────────

const TABS = [
    { key: '',             label: 'All Logs' },
    { key: 'student',      label: 'Students' },
    { key: 'college_admin',label: 'College Admin' },
    { key: 'admin',        label: 'Admin' },
    { key: 'recruiter',    label: 'Recruiter' },
];

const AccessLogs = () => {
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
    const [pageSize, setPageSize] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('');

    const [filters, setFilters] = useState({
        action: '',
        success: '',
        role: '',
        dateFrom: '',
        dateTo: '',
        search: '',
    });

    // Sync active tab → role filter
    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
        setFilters(prev => ({ ...prev, role: tabKey }));
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters(prev => ({ ...prev, search: searchTerm }));
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchLogs = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page,
                limit: pageSize,
                ...(filters.action && { action: filters.action }),
                ...(filters.success !== '' && { success: filters.success }),
                ...(filters.role && { role: filters.role }),
                ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
                ...(filters.dateTo && { dateTo: filters.dateTo }),
                ...(filters.search && { search: filters.search }),
            });

            const { data } = await api.get(`/logs/auth?${params}`);
            if (data.success) {
                setLogs(data.data);
                setPagination({ page, pages: data.pagination.pages, total: data.pagination.total });
                setStats(data.stats);
            }
        } catch (err) {
            toast.error('Failed to load access logs');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [filters, pageSize]);

    useEffect(() => { fetchLogs(1); }, [fetchLogs]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
        setSearchTerm('');
        setActiveTab('');
        setFilters({ action: '', success: '', role: '', dateFrom: '', dateTo: '', search: '' });
    };

    const handleExportAll = async () => {
        try {
            const params = new URLSearchParams({
                export: 'true',
                ...(filters.action && { action: filters.action }),
                ...(filters.success !== '' && { success: filters.success }),
                ...(filters.role && { role: filters.role }),
                ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
                ...(filters.dateTo && { dateTo: filters.dateTo }),
                ...(filters.search && { search: filters.search }),
            });

            const { data } = await api.get(`/logs/auth?${params}`);
            if (data.success) {
                exportToCSV(data.data);
            }
        } catch (err) {
            toast.error('Failed to export all logs');
            console.error(err);
        }
    };

    return (
        <div className="space-y-6 pb-24">

            {/* Header */}
            <div className="glass dark:bg-[#002147]/80 rounded-xl shadow-md border border-white/30 dark:border-[#1a3884]/30 p-4 mb-6 animate-fade-in">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="p-2.5 bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-white hover:bg-[#00152E]/5 dark:hover:bg-[#1a3884] rounded-xl transition-all border border-gray-200 dark:border-[#1a3884]/45 group shadow-sm flex items-center justify-center shrink-0"
                            title="Back to Dashboard"
                        >
                            <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-[#00152E] dark:text-white mb-1">Access & Audit Logs</h1>
                            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                <ShieldAlert size={14} className="text-[#1a3884]" />
                                <span className="font-medium">All authentication events — logins, failures, OTP attempts, and logouts</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto justify-start sm:justify-end">
                        <button
                            onClick={() => fetchLogs(pagination.page)}
                            className="flex-1 sm:flex-initial btn-secondary flex items-center justify-center gap-2 text-sm px-3 py-2 whitespace-nowrap bg-white dark:bg-[#00152e] text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-[#1a3884]/50 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20"
                        >
                            <RefreshCw size={14} />
                            <span className="hidden sm:inline">Refresh</span>
                            <span className="sm:hidden">Refresh</span>
                        </button>
                        <button
                            onClick={handleExportAll}
                            className="flex items-center gap-2 text-sm px-4 py-2 bg-[#1a3884] hover:bg-[#00152E] dark:bg-[#1a3884] dark:hover:bg-[#00152E] text-white font-semibold rounded-xl transition-all shadow-md shadow-[#1a3884]/15 hover:shadow-lg hover:shadow-[#1a3884]/20 transform hover:-translate-y-0.5 transition-colors border border-[#00152E]/20"
                        >
                            <Download size={14} />
                            <span className="hidden sm:inline">Export All</span>
                            <span className="sm:hidden">Export</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <StatCard
                    label="Total Logs"
                    value={pagination.total || '…'}
                    Icon={Clock}
                />
                <StatCard
                    label="Successful Logins"
                    value={stats?.totalLogins ?? '…'}
                    Icon={CheckCircle}
                />
                <StatCard
                    label="Failed Attempts"
                    value={stats?.totalFailed ?? '…'}
                    Icon={XCircle}
                />
                <StatCard
                    label="Unique IPs"
                    value={stats?.uniqueIps ?? '…'}
                    Icon={Globe}
                    subtext="Across all time"
                />
            </div>

            {/* ── Role Tabs ─────────────────────────────────────────────────── */}
            <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/35 shadow-sm overflow-hidden">
                <div className="flex overflow-x-auto scrollbar-hide">
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => handleTabChange(tab.key)}
                            className={`flex-shrink-0 px-5 py-3 text-sm font-semibold border-b-2 transition-all duration-200 whitespace-nowrap ${
                                activeTab === tab.key
                                    ? 'border-[#1a3884] text-[#1a3884] dark:text-white dark:border-white bg-[#1a3884]/5 dark:bg-[#1a3884]/30'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-[#1a3884] dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#001f40]/40'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/35 shadow-sm p-5">
                <div className="flex flex-wrap items-center gap-3 w-full">
                    <div className="flex items-center gap-1.5 text-gray-500 shrink-0">
                        <Filter size={16} />
                        <span className="text-sm font-medium">Filters</span>
                    </div>

                    {/* Search */}
                    <div className="relative w-full sm:flex-1 sm:max-w-xs md:max-w-sm">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder="Search email / name / IP…"
                            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#1a3884] bg-gray-50 focus:bg-white transition-all outline-none"
                        />
                        {searchTerm && (
                            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                                <XCircle size={14} />
                            </button>
                        )}
                    </div>

                    {/* Action Type */}
                    <select
                        value={filters.action}
                        onChange={e => handleFilterChange('action', e.target.value)}
                        className="px-3 py-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/45 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1a3884] bg-gray-50 dark:bg-[#002147] w-full sm:w-auto min-w-[140px]"
                    >
                        <option value="">All Actions</option>
                        <option value="Login">Login (Success)</option>
                        <option value="LoginFailed">Login Failed</option>
                        <option value="LoginBlocked">Login Blocked</option>
                        <option value="OtpFailed">OTP Failed</option>
                        <option value="Logout">Logout</option>
                    </select>

                    {/* Success filter */}
                    <select
                        value={filters.success}
                        onChange={e => handleFilterChange('success', e.target.value)}
                        className="px-3 py-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/45 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1a3884] bg-gray-50 dark:bg-[#002147] w-full sm:w-auto min-w-[120px]"
                    >
                        <option value="">All Statuses</option>
                        <option value="true">Successful</option>
                        <option value="false">Failed</option>
                    </select>



                    {/* Date From */}
                    <input
                        type="date"
                        value={filters.dateFrom}
                        onChange={e => handleFilterChange('dateFrom', e.target.value)}
                        className="px-3 py-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/45 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1a3884] bg-gray-50 dark:bg-[#002147] w-full sm:w-auto"
                        title="From Date"
                    />
                    <span className="text-gray-400 text-sm">to</span>
                    {/* Date To */}
                    <input
                        type="date"
                        value={filters.dateTo}
                        onChange={e => handleFilterChange('dateTo', e.target.value)}
                        className="px-3 py-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/45 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1a3884] bg-gray-50 dark:bg-[#002147] w-full sm:w-auto"
                        title="To Date"
                    />

                    {/* Clear */}
                    {Object.values(filters).some(Boolean) && (
                        <button
                            onClick={clearFilters}
                            className="text-xs text-red-500 dark:text-red-400 font-semibold hover:text-red-700 dark:hover:text-red-300 transition px-2 ml-auto sm:ml-0"
                        >
                            Reset
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/35 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-[#1a3884]/30 bg-gray-50/50 dark:bg-[#001f40]/20">
                    <h2 className="font-semibold text-gray-800 dark:text-gray-100">Audit Logs Directory</h2>
                    <span className="text-sm text-slate-400 dark:text-slate-200">
                        {loading ? 'Fetching...' : `Showing ${logs.length} of ${pagination.total} total`}
                    </span>
                </div>

                {loading ? (
                    <div className="p-16 flex flex-col items-center gap-4 text-gray-400">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1a3884]" />
                        <p className="text-sm">Loading access logs…</p>
                    </div>
                ) : logs.length === 0 ? (
                    <div className="p-16 flex flex-col items-center gap-3 text-gray-400">
                        <ShieldAlert size={40} className="text-gray-300" />
                        <p className="text-sm font-medium">No logs matched your filters.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-[#001f40]/80 border-b border-gray-100 dark:border-[#1a3884]/20">
                                    {['Timestamp', 'User', 'Role', 'Action', 'Status', 'IP Address', 'Device Info', 'Details'].map(h => (
                                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wide whitespace-nowrap">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-[#1a3884]/20 bg-white dark:bg-[#002147]">
                                {logs.map((log, idx) => {
                                    const actionMeta = ACTION_LABELS[log.action] || {
                                        label: log.action,
                                        color: 'bg-gray-100 text-gray-700',
                                        Icon: Clock
                                    };
                                    const ActionIcon = actionMeta.Icon;
                                    return (
                                        <tr key={log._id || idx} className="hover:bg-[#00152E]/5 dark:hover:bg-[#001f40]/80 transition-colors border-b border-gray-100 dark:border-[#1a3884]/20">
                                            {/* Timestamp */}
                                            <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300 text-xs font-medium">
                                                {fmt(log.createdAt)}
                                            </td>

                                            {/* User */}
                                            <td className="px-4 py-3">
                                                {log.user ? (
                                                    <div>
                                                        <p className="font-semibold text-gray-800 dark:text-gray-200">{log.user.fullName}</p>
                                                        <p className="text-xs text-slate-400 dark:text-slate-200">{log.user.email}</p>
                                                    </div>
                                                ) : log.userName || log.userEmail ? (
                                                    <div>
                                                        <p className="font-semibold text-gray-800 dark:text-gray-200">{log.userName || 'Unknown'}</p>
                                                        <p className="text-xs text-slate-400 dark:text-slate-200">{log.userEmail || log.targetEmail}</p>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <p className="text-xs italic text-gray-400 dark:text-gray-300">Unknown</p>
                                                        {log.targetEmail && (
                                                            <p className="text-xs text-orange-500 dark:text-orange-400 font-medium">{log.targetEmail}</p>
                                                        )}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Role */}
                                            <td className="px-4 py-3">
                                                {log.user?.role || log.userRole ? (() => {
                                                    const r = log.user?.role || log.userRole;
                                                    return (
                                                        <span className={`inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[r] || 'bg-gray-100 text-gray-600'}`}>
                                                            {r.replace('_', ' ')}
                                                        </span>
                                                    );
                                                })() : <span className="text-gray-300 dark:text-gray-600">—</span>}
                                            </td>

                                            {/* Action badge */}
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${actionMeta.color}`}>
                                                    <ActionIcon size={12} className="shrink-0" />
                                                    {actionMeta.label}
                                                </span>
                                            </td>

                                            {/* Success / Failed */}
                                            <td className="px-4 py-3">
                                                {log.success
                                                    ? <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 text-xs font-semibold"><CheckCircle size={13} /> Success</span>
                                                    : <span className="inline-flex items-center gap-1 text-red-500 dark:text-red-400 text-xs font-semibold"><XCircle size={13} /> Failed</span>
                                                }
                                            </td>

                                            {/* IP */}
                                            <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300 font-medium whitespace-nowrap">
                                                {formatIP(log.ipAddress)}
                                            </td>

                                            {/* Device */}
                                            <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-300" title={log.deviceInfo}>
                                                {log.deviceInfo ? (
                                                    <span className="flex items-center gap-1">
                                                        <Monitor size={12} className="shrink-0 text-gray-400 dark:text-gray-300" />
                                                        {log.deviceInfo}
                                                    </span>
                                                ) : '—'}
                                            </td>

                                            {/* Details */}
                                            <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-300" title={log.details}>
                                                {log.details || '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-600 dark:text-slate-200 pb-4">
                    <span className="text-center sm:text-left">
                        Showing page <strong>{pagination.page}</strong> of <strong>{pagination.pages}</strong>
                        {' '}({pagination.total} total records)
                    </span>
                    <div className="flex flex-wrap items-center justify-center gap-2">
                        <select
                            value={pageSize}
                            onChange={e => setPageSize(Number(e.target.value))}
                            className="px-2 py-1.5 rounded-lg border border-gray-200 dark:border-[#1a3884]/45 text-xs bg-gray-50 dark:bg-[#002147] text-gray-700 dark:text-gray-200"
                        >
                            {[10, 25, 50, 100].map(s => (
                                <option key={s} value={s}>{s} / page</option>
                            ))}
                        </select>
                        <button
                            onClick={() => fetchLogs(pagination.page - 1)}
                            disabled={pagination.page <= 1}
                            className="p-1.5 rounded-lg border border-gray-200 dark:border-[#1a3884]/40 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-[#002d5c] text-gray-700 dark:text-gray-200 transition"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        {/* Page numbers — show a window of 5 */}
                        {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                            const start = Math.max(1, Math.min(pagination.page - 2, pagination.pages - 4));
                            const pg = start + i;
                            if (pg > pagination.pages) return null;
                            return (
                                <button
                                    key={pg}
                                    onClick={() => fetchLogs(pg)}
                                    className={`w-8 h-8 rounded-lg text-xs font-medium border transition ${pg === pagination.page
                                        ? 'bg-[#1a3884] text-white border-[#1a3884]'
                                        : 'border-gray-200 dark:border-[#1a3884]/40 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#002d5c]'}`}
                                >
                                    {pg}
                                </button>
                            );
                        })}
                        <button
                            onClick={() => fetchLogs(pagination.page + 1)}
                            disabled={pagination.page >= pagination.pages}
                            className="p-1.5 rounded-lg border border-gray-200 dark:border-[#1a3884]/40 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-[#002d5c] text-gray-700 dark:text-gray-200 transition"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}

            {/* Footer note */}
            <p className="text-center text-xs text-slate-400 dark:text-slate-300 pb-4">
                Audit logs are recorded for all authentication events including login attempts, OTP failures, and logouts.
                Logs are retained indefinitely for compliance.
            </p>
        </div>
    );
};

export default AccessLogs;
